import { createSlice } from '@reduxjs/toolkit'
import {
  ELECTRICITY_METER_KEY,
  SECOND_ELECTRICITY_METER_KEY,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/Common/constants'
import contactSubscriptionData from '@/containers/SubscriptionFormPageNew/SubscriptionForm/Contact/contactSubscriptionData'
import { parseTab } from '@/shared/tools/activeUtil'
import addressSubscriptionData from './Address/addressSubscriptionData'

const initialState = {
  contact: { ...contactSubscriptionData },
  addresses: {
    new: {},
    config: {
      addressList: [],
      addressIsLoading: false,
      caption: null,
      errorText: null,
      errors: {},
      addressIsSaving: false,
      showAddressFields: false,
      showEnergyLandscape: false,
      buildingPermitModal: false,
      cadastralFieldsModal: false,
      lastHolderFieldsModal: false,
      deleteAddressModal: false,
      deleteAddressBlockedModal: false,
      addressToDeleteId: null,
      deleteAddressIsLoading: false,
      // Per-address dirty flags. Key = addressId (or 'new'), value = boolean.
      dirtyMap: {},
    },
  },
  contracts: {},
  comments: [],
  additionalSalesConfig: {
    salesConfigLoaded: false,
    existingSalesLoaded: false,
    groupConfig: {},
    sales: {},
  },
  additionalSales: {},
  openedContracts: [],
  config: {
    activeTab: 'client',
    addContractModal: false,
    createContractSelectedType: null,
    activeAddress: null,
    contactId: null,
    isImportingContactFromZoho: false,
  },
  communicationBoard: {
    loading: false,
    error: null,
    sections: [],
    availableSections: [],
  },
  pdlSearchData: {
    address: {},
    searchPodResponses: [],
    searchPodResponsesOriginal: [],
    viewPage: 'search',
    occupant: '',
    serialNumber: '',
    complement: '',
    building: '',
    locality: '',
    contractType: 'part',
    matricule: '',
    pdlSelected: '',
    isOpen: false,
    isLoading: false,
    page: 1,
    errorMessage: null,
    podSearchDataId: null,
  },
  pceSearchData: {
    address: {},
    searchPodResponses: [],
    searchPodResponsesOriginal: [],
    viewPage: 'search',
    occupant: '',
    serialNumber: '',
    pceSelected: '',
    isOpen: false,
    isLoading: false,
    page: 1,
    errorMessage: null,
    podSearchDataId: null,
  },
  scheduledCallback: {
    loading: false,
    success: false,
    message: null,
    error: null,
    lastCreatedAt: null,
    googleCalendarUrl: null,
    form: {
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      scheduledDate: '',
      hour: '',
      minutes: '',
      assigned: 'self',
      isValid: false,
    },
    upcoming: {
      isLoading: false,
      loadedForZohoId: null,
      items: [],
      error: null,
    },
  },
  clubSelectra: {
    loading: false,
    success: false,
    message: null,
    error: null,
  },
  fidelityMaximizer: {
    loading: false,
    loaded: false,
    enabled: false,
    checklist: {
      nlbp_classic: false,
      nlbp_personalized: false,
      myselectra: false,
      app_downloaded: false,
    },
    isPdlEligible: false,
    scenarioKey: null,
    actionKey: null,
    actionAvailable: false,
    actionLoading: false,
    error: null,
  },
  doorToDoorPartnerChecklist: {
    checkedTitles: {
      introduction: false,
      identity: false,
      presentation: false,
      visitPurpose: false,
      recap: false,
    },
    expanded: {
      introduction: false,
      identity: false,
      presentation: false,
      visitPurpose: false,
      recap: false,
    },
  },
}

const initializeContractDataHandler = (state, action) => {
  state.contact = action.payload.contact
  state.addresses = {
    ...state.addresses,
    ...action.payload.addresses,
  }
  state.config.contactId = action.payload.contactId
  state.contracts = action.payload.contracts
  state.comments = action.payload.comments
  state.recentBankings = action.payload.recentBankings ?? []
  // Initial load from backend: everything is "clean".
  if (state.contact?.config) {
    state.contact.config.isDirty = false
  }
  if (state.addresses?.config) {
    state.addresses.config.dirtyMap = {}
  }
}

const addRecentBankingHandler = (state, action) => {
  const { iban, bic } = action.payload
  if (!iban) return
  const exists = (state.recentBankings ?? []).some(banking => banking.iban === iban)
  if (!exists) {
    state.recentBankings = [{ id: Date.now(), iban, bic }, ...(state.recentBankings ?? [])]
  }
}

const importContractFromZohoDataHandler = (state, action) => {
  const { contact, addresses } = action.payload
  const originalPreferences = state.contact.preferences || {}
  const originalConfig = state.contact.config || {}

  if (contact.preferences) {
    delete contact.preferences.dataCollectionAuth
  }

  state.contact = {
    ...state.contact,
    ...contact,
    preferences: {
      ...originalPreferences,
      ...contact.preferences,
    },
    config: {
      ...originalConfig,
    },
  }

  addresses.forEach(address => {
    state.addresses[address.id] = address
  })
}

const toggleGroupVisibilityHandler = (state, action) => {
  const group = action.payload
  state.additionalSalesConfig.groupConfig[group].visible = !state.additionalSalesConfig.groupConfig[group].visible
}

const updateAdditionalSalesConfigHandler = (state, action) => {
  state.additionalSalesConfig = {
    ...state.additionalSalesConfig,
    ...action.payload,
  }
}

// example of dispatch: dispatch(updateAdditionalSalesData({data: {test: 'test'}, name: 'voltalis'}))
const updateAdditionalSalesDataHandler = (state, action) => {
  // really weird stuff to don't update from status update for AXA Assistance/Smartphone if it's not the same address currently selected
  // then update in all AXA sales array the needed data
  const payload = action.payload.data || {}
  const perAddressSale = {
    leadAssistance: 'allAssistancesSales',
    leadAxaSmartphone: 'allAxaSmartphonesSales',
  }[action.payload.name]
  if (perAddressSale && payload.infoAttributes?.addressId?.length > 0) {
    const currentData = state.additionalSales[action.payload.name] || {}
    if (currentData?.addressId?.toString() !== payload.infoAttributes?.addressId?.toString()) {
      const allSales = state.additionalSales[perAddressSale] || []
      const saleIndex = allSales.findIndex(
        sale => sale?.addressId?.toString() === payload.infoAttributes?.addressId?.toString(),
      )
      if (saleIndex !== -1) {
        state.additionalSales[perAddressSale][saleIndex] = {
          ...state.additionalSales[perAddressSale][saleIndex],
          ...payload,
        }
      } else {
        state.additionalSales[perAddressSale].push({
          ...payload,
          ...payload.infoAttributes,
        })
      }
      return
    }
  }
  state.additionalSales[action.payload.name] = {
    ...state.additionalSales[action.payload.name],
    ...action.payload.data,
  }
}

const initAdditionalSalesHandler = (state, action) => {
  state.additionalSales = {
    ...action.payload,
  }
}

// dispatch(updateClientDataHandler({data: {firstName: 'Tomas'}, name: 'personal'}))
// Pass `_skipDirty: true` from sagas/listeners that update internal contact
// state to avoid flipping the dirty flag.
const updateClientDataHandler = (state, action) => {
  state.contact[action.payload.name] = {
    ...state.contact[action.payload.name],
    ...action.payload.data,
  }
  if (action.payload._skipDirty === true) return
  // Mark the contact module as dirty only when the user data sections change.
  // Internal config updates (loaders, errors, isSaving…) must not flip the dirty flag.
  if (action.payload.name !== 'config') {
    state.contact.config.isDirty = true
  }
}

const resetContactDirtyHandler = state => {
  state.contact.config.isDirty = false
}

const addClientErrorHandler = (state, action) => {
  state.contact.config.errors = {
    ...state.contact.config.errors,
    [action.payload.key]: [action.payload.message],
  }
}

const removeClientErrorHandler = (state, action) => {
  const newErrorState = { ...state.contact.config.errors }
  if (newErrorState[action.payload]) {
    delete newErrorState[action.payload]
  }
  state.contact.config.errors = newErrorState
}

// Internal meter keys mutated by sagas / websocket listeners / hooks (NOT
// the user). When an update batch contains only these keys, we keep isDirty
// unchanged so background events (eligibility lookups, payload writes,
// isSaving toggles, UI-only flags…) never flag the module as unsaved.
//
// Notably:
//   - pdlEligibilityData / pceEligibilityData are payloads returned by the
//     backend after the user submits a pdlNo/pceNo. The eligibility WRITE is
//     internal (saga side-effect). The original user edit (pdlNo) already
//     flips dirty on its own path, so we don't lose dirty tracking by
//     ignoring the eligibility writes.
//   - showPdlConfirmation is a UI-only flag toggled by useMeterOwnerRule
//     when the user opens an energy contract tab. It is NOT a user edit, so
//     it must not flip the client-tab unsaved-changes warning when simply
//     navigating between tabs.
const METER_INTERNAL_KEYS = [
  'isSaving',
  'pdlEligibilityPending',
  'pdlEligibilityLoading',
  'pdlEligibilityData',
  'pceEligibilityPending',
  'pceEligibilityLoading',
  'pceEligibilityData',
  'showPdlConfirmation',
]

const isOnlyInternalUpdate = (data, internalKeys) => {
  const keys = Object.keys(data || {})
  return keys.length > 0 && keys.every(key => internalKeys.includes(key))
}

// `_skipDirty: true` opts out from the dirty marking. Sagas that perform
// background updates (cityEligibility, consumption estimation, post-save data
// rewrite, hasElectricityMeter/hasGasMeter side-effect, websocket prefill…)
// must pass it so the global "unsaved changes" warning stays accurate.
const deleteAddressFromStateHandler = (state, action) => {
  const { id } = action.payload
  delete state.addresses[id]
}

const updateAddressDataHandler = (state, action) => {
  const addressKey = action.payload.id ?? 'new'
  state.addresses[addressKey] = {
    ...state.addresses[addressKey],
    ...action.payload.data,
  }
  if (action.payload._skipDirty === true) return
  // Any user-facing field-level address update marks the address as dirty.
  state.addresses.config.dirtyMap = {
    ...(state.addresses.config.dirtyMap || {}),
    [addressKey]: true,
  }
}

const resetAddressDirtyHandler = (state, action) => {
  const addressKey = action.payload?.id ?? 'new'
  state.addresses.config.dirtyMap = {
    ...(state.addresses.config.dirtyMap || {}),
    [addressKey]: false,
  }
}

const updateElectricityMeterAddressDataHandler = (state, action) => {
  const addressKey = action.payload.id ?? 'new'
  const meterKey = action.payload.meterKey ?? ELECTRICITY_METER_KEY
  state.addresses[addressKey][meterKey] = {
    ...(state.addresses[addressKey][meterKey] || {}),
    ...action.payload.data,
  }
  if (action.payload._skipDirty === true) return
  if (!isOnlyInternalUpdate(action.payload.data, METER_INTERNAL_KEYS)) {
    state.addresses[addressKey][meterKey].isDirty = true
  }
}

const resetElectricityMeterDirtyHandler = (state, action) => {
  const addressKey = action.payload?.id ?? 'new'
  ;[ELECTRICITY_METER_KEY, SECOND_ELECTRICITY_METER_KEY].forEach(meterKey => {
    if (state.addresses[addressKey]?.[meterKey]) {
      state.addresses[addressKey][meterKey].isDirty = false
    }
  })
}

const updateGasMeterAddressDataHandler = (state, action) => {
  const addressKey = action.payload.id ?? 'new'
  state.addresses[addressKey].gasMeter = {
    ...(state.addresses[addressKey].gasMeter || {}),
    ...action.payload.data,
  }
  if (action.payload._skipDirty === true) return
  if (!isOnlyInternalUpdate(action.payload.data, METER_INTERNAL_KEYS)) {
    state.addresses[addressKey].gasMeter.isDirty = true
  }
}

const resetGasMeterDirtyHandler = (state, action) => {
  const addressKey = action.payload?.id ?? 'new'
  if (state.addresses[addressKey]?.gasMeter) {
    state.addresses[addressKey].gasMeter.isDirty = false
  }
}

// Resets every dirty flag tied to the client tab. Used right before applying
// a freshly loaded backend state so nothing looks "unsaved".
const resetClientTabDirtyHandler = state => {
  state.contact.config.isDirty = false
  state.addresses.config.dirtyMap = {}
  Object.keys(state.addresses).forEach(addressKey => {
    if (addressKey === 'config') return
    if (state.addresses[addressKey]?.electricityMeter) {
      state.addresses[addressKey].electricityMeter.isDirty = false
    }
    if (state.addresses[addressKey]?.gasMeter) {
      state.addresses[addressKey].gasMeter.isDirty = false
    }
  })
}

const updateAddressesConfigDataHandler = (state, action) => {
  state.addresses.config = {
    ...state.addresses.config,
    ...action.payload,
  }
}

const addAddressErrorHandler = (state, action) => {
  const previousAddressErrorsState = state.addresses.config.errors[action.payload.id] ?? {}
  state.addresses.config.errors = {
    ...state.addresses.config.errors,
    [action.payload.id]: { ...previousAddressErrorsState, ...action.payload.data },
  }
}

const removeAddressErrorHandler = (state, action) => {
  const newState = { ...state.addresses.config.errors }

  if (newState[action.payload.id]) {
    delete newState[action.payload.id][action.payload.key]
  }
  state.addresses.config.errors = newState
}

const resetAddressDataHandler = (state, action) => {
  const addressKey = action.payload.id ?? 'new'
  const contactId = state.addresses[addressKey]?.contactId || null
  const addressId = state.addresses[addressKey]?.id || 0
  state.addresses[addressKey] = {
    ...addressSubscriptionData,
    id: addressId,
    contactId,
    cityEligibility: {},
    gasAvailable: false,
    electricityAvailable: false,
  }
}

const updateContractCharacteristicsDataHandler = (state, action) => {
  if (!state.contracts[action.payload.id]) {
    return
  }
  state.contracts[action.payload.id].characteristics = {
    ...(state.contracts[action.payload.id].characteristics || {}),
    ...action.payload.data,
  }
}

const updateContractDefaultDataHandler = (state, action) => {
  if (!state.contracts[action.payload.id]) {
    return
  }
  state.contracts[action.payload.id] = {
    ...(state.contracts[action.payload.id] || {}),
    ...action.payload.data,
  }
}

const updateContractConsumptionDataHandler = (state, action) => {
  if (!state.contracts[action.payload.id]) {
    return
  }
  state.contracts[action.payload.id].consumption = {
    ...(state.contracts[action.payload.id].consumption || {}),
    ...action.payload.data,
    prefilled: {
      ...(state.contracts[action.payload.id].consumption?.prefilled || {}),
      ...action.payload.data.prefilled,
    },
  }
}

const updateContractPropaneDataHandler = (state, action) => {
  if (!state.contracts[action.payload.id]) {
    return
  }
  state.contracts[action.payload.id].propane = {
    ...(state.contracts[action.payload.id].propane || {}),
    ...action.payload.data,
  }
}

const updateContractElecDataHandler = (state, action) => {
  if (!state.contracts[action.payload.id]) {
    return
  }
  state.contracts[action.payload.id].elec = {
    ...(state.contracts[action.payload.id].elec || {}),
    ...action.payload.data,
  }
}

const addContractErrorsHandler = (state, action) => {
  if (!state.contracts[action.payload.id]) {
    return
  }
  state.contracts[action.payload.id].errors = {
    ...(state.contracts[action.payload.id].errors || {}),
    ...action.payload.data,
  }
}

const resetContractErrorsHandler = (state, action) => {
  if (!state.contracts[action.payload.id]) {
    return
  }
  state.contracts[action.payload.id].errors = {}
}

const removeErrorFromContractHandler = (state, action) => {
  if (!state.contracts[action.payload.id]) {
    return
  }
  const currentErrors = state.contracts[action.payload.id].errors || {}
  if (currentErrors[action.payload.data]) {
    delete currentErrors[action.payload.data]
    state.contracts[action.payload.id].errors = currentErrors
  }
}

const updateContractGasDataHandler = (state, action) => {
  if (!state.contracts[action.payload.id]) {
    return
  }
  state.contracts[action.payload.id].gas = {
    ...(state.contracts[action.payload.id].gas || {}),
    ...action.payload.data,
  }
}

const updateContractDetailsDataHandler = (state, action) => {
  if (!state.contracts[action.payload.id]) {
    return
  }
  state.contracts[action.payload.id].contractDetails = {
    ...(state.contracts[action.payload.id].contractDetails || {}),
    ...action.payload.data,
  }
}

const updateContractBillingDataHandler = (state, action) => {
  if (!state.contracts[action.payload.id]) {
    return
  }
  state.contracts[action.payload.id].billing = {
    ...(state.contracts[action.payload.id].billing || {}),
    ...action.payload.data,
  }
}

const updateContractAddressIdHandler = (state, action) => {
  state.contracts[action.payload.id].addressId = action.payload.addressId
}

const updateContractBillingAddressIdHandler = (state, action) => {
  state.contracts[action.payload.id].billingAddressId = action.payload.billingAddressId
}

const updateContractResidenceAddressIdHandler = (state, action) => {
  state.contracts[action.payload.id].residenceAddressId = action.payload.residenceAddressId
}

const updateContractConfigDataHandler = (state, action) => {
  if (!state.contracts[action.payload.id]) {
    return
  }
  state.contracts[action.payload.id].config = {
    ...(state.contracts[action.payload.id].config || {}),
    ...action.payload.data,
  }
}

const updateActiveTabHandler = (state, action) => {
  state.config.activeTab = action.payload
}

const updateActiveAddressHandler = (state, action) => {
  state.config.activeAddress = action.payload
}

const updateConfigHandler = (state, action) => {
  state.config = {
    ...state.config,
    ...action.payload,
  }
}

const updatePdlSearchDataHandler = (state, action) => {
  state.pdlSearchData = {
    ...state.pdlSearchData,
    ...action.payload,
  }
}

const updatePceSearchDataHandler = (state, action) => {
  state.pceSearchData = {
    ...state.pceSearchData,
    ...action.payload,
  }
}

const addContractToOpenedContractsHandler = (state, action) => {
  const keyName = action.payload.id + '-' + action.payload.type
  state.contracts[action.payload.id] = action.payload
  if (!state.openedContracts.includes(action.payload.id)) {
    state.openedContracts.push(action.payload.id)
  }
  state.config.activeTab = keyName
}

const removeContractFromOpenedContractsHandler = (state, action) => {
  state.openedContracts = state.openedContracts.filter(contractId => contractId !== action.payload.id)
  const { id } = parseTab(state.config.activeTab)

  if (id === action.payload.id) {
    state.config.activeTab = 'client'
  }
}

const updateCommunicationBoardDataHandler = (state, action) => {
  state.communicationBoard = {
    ...state.communicationBoard,
    ...action.payload,
  }
}

const setCommunicationBoardErrorHandler = (state, action) => {
  state.communicationBoard.error = action.payload
}

const updateScheduledCallbackHandler = (state, action) => {
  const payload = action.payload || {}
  const { form, ...rest } = payload

  state.scheduledCallback = {
    ...state.scheduledCallback,
    ...rest,
  }
  if (form) {
    state.scheduledCallback.form = {
      ...state.scheduledCallback.form,
      ...form,
    }
  }
}

const updateClubSelectraHandler = (state, action) => {
  state.clubSelectra = {
    ...state.clubSelectra,
    ...action.payload,
  }
}

const updateFidelityMaximizerHandler = (state, action) => {
  state.fidelityMaximizer = {
    ...state.fidelityMaximizer,
    ...action.payload,
  }
}

const addCommentToContactHandler = (state, action) => {
  state.comments.push({ ...action.payload })
}

const updateCommentForContactHandler = (state, action) => {
  const { id, comment } = action.payload
  const target = state.comments.find(c => c.id === id)
  if (target) {
    target.comment = comment
  }
}

const removeCommentFromContactHandler = (state, action) => {
  const { id } = action.payload
  state.comments = state.comments.filter(c => c.id !== id)
}

const updateDoorToDoorPartnerChecklistCheckedTitleHandler = (state, action) => {
  const { key, checked } = action.payload
  state.doorToDoorPartnerChecklist.checkedTitles[key] = checked
}

const toggleDoorToDoorPartnerChecklistExpandedHandler = (state, action) => {
  const key = action.payload
  state.doorToDoorPartnerChecklist.expanded[key] = !state.doorToDoorPartnerChecklist.expanded[key]
}

export const SubscriptionFormSlice = createSlice({
  name: 'formV2',
  initialState,
  reducers: {
    initializeContractData: initializeContractDataHandler,
    addRecentBanking: addRecentBankingHandler,
    importContractFromZohoData: importContractFromZohoDataHandler,
    updateClientData: updateClientDataHandler,
    deleteAddressFromState: deleteAddressFromStateHandler,
    updateAddressData: updateAddressDataHandler,
    updateElectricityMeterAddressData: updateElectricityMeterAddressDataHandler,
    updateGasMeterAddressData: updateGasMeterAddressDataHandler,
    resetAddressData: resetAddressDataHandler,
    updateAddressesConfigData: updateAddressesConfigDataHandler,
    addAddressError: addAddressErrorHandler,
    removeAddressError: removeAddressErrorHandler,
    updateContractCharacteristicsData: updateContractCharacteristicsDataHandler,
    updateContractConsumptionData: updateContractConsumptionDataHandler,
    updateContractElecData: updateContractElecDataHandler,
    updateContractGasData: updateContractGasDataHandler,
    updateContractPropaneData: updateContractPropaneDataHandler,
    updateContractDetailsData: updateContractDetailsDataHandler,
    updateContractBillingData: updateContractBillingDataHandler,
    updateContractAddressId: updateContractAddressIdHandler,
    updateContractBillingAddressId: updateContractBillingAddressIdHandler,
    updateContractResidenceAddressId: updateContractResidenceAddressIdHandler,
    addContractToOpenedContracts: addContractToOpenedContractsHandler,
    updateContractConfigData: updateContractConfigDataHandler,
    removeContractFromOpenedContracts: removeContractFromOpenedContractsHandler,
    updateConfig: updateConfigHandler,
    updateActiveTab: updateActiveTabHandler,
    updateActiveAddress: updateActiveAddressHandler,
    addClientError: addClientErrorHandler,
    removeClientError: removeClientErrorHandler,
    addContractErrors: addContractErrorsHandler,
    resetContractErrors: resetContractErrorsHandler,
    removeErrorFromContract: removeErrorFromContractHandler,
    updateContractDefaultData: updateContractDefaultDataHandler,
    toggleGroupVisibility: toggleGroupVisibilityHandler,
    updateAdditionalSalesConfig: updateAdditionalSalesConfigHandler,
    updateAdditionalSalesData: updateAdditionalSalesDataHandler,
    initAdditionalSales: initAdditionalSalesHandler,
    updateCommunicationBoardData: updateCommunicationBoardDataHandler,
    setCommunicationBoardError: setCommunicationBoardErrorHandler,
    updatePdlSearchData: updatePdlSearchDataHandler,
    updatePceSearchData: updatePceSearchDataHandler,
    updateScheduledCallback: updateScheduledCallbackHandler,
    updateClubSelectra: updateClubSelectraHandler,
    updateFidelityMaximizer: updateFidelityMaximizerHandler,
    addCommentToContact: addCommentToContactHandler,
    updateCommentForContact: updateCommentForContactHandler,
    removeCommentFromContact: removeCommentFromContactHandler,
    updateDoorToDoorPartnerChecklistCheckedTitle: updateDoorToDoorPartnerChecklistCheckedTitleHandler,
    toggleDoorToDoorPartnerChecklistExpanded: toggleDoorToDoorPartnerChecklistExpandedHandler,
    resetContactDirty: resetContactDirtyHandler,
    resetAddressDirty: resetAddressDirtyHandler,
    resetElectricityMeterDirty: resetElectricityMeterDirtyHandler,
    resetGasMeterDirty: resetGasMeterDirtyHandler,
    resetClientTabDirty: resetClientTabDirtyHandler,
  },
})

export const {
  initializeContractData,
  addRecentBanking,
  importContractFromZohoData,
  updateClientData,
  addClientError,
  removeClientError,
  deleteAddressFromState,
  updateAddressData,
  updateElectricityMeterAddressData,
  updateGasMeterAddressData,
  resetAddressData,
  updateAddressesConfigData,
  addAddressError,
  removeAddressError,
  updateContractCharacteristicsData,
  updateContractConsumptionData,
  updateContractElecData,
  updateContractGasData,
  updateContractPropaneData,
  updateContractDetailsData,
  updateContractBillingData,
  updateContractAddressId,
  updateContractBillingAddressId,
  updateContractResidenceAddressId,
  updateContractConfigData,
  addContractToOpenedContracts,
  removeContractFromOpenedContracts,
  updateConfig,
  updateActiveTab,
  updateActiveAddress,
  addScript,
  removeScript,
  addContractErrors,
  removeErrorFromContract,
  resetContractErrors,
  updateContractDefaultData,
  toggleGroupVisibility,
  updateAdditionalSalesConfig,
  updateAdditionalSalesData,
  initAdditionalSales,
  updateCommunicationBoardData,
  setCommunicationBoardError,
  updatePdlSearchData,
  updatePceSearchData,
  updateScheduledCallback,
  updateClubSelectra,
  updateFidelityMaximizer,
  addCommentToContact,
  updateCommentForContact,
  removeCommentFromContact,
  updateDoorToDoorPartnerChecklistCheckedTitle,
  toggleDoorToDoorPartnerChecklistExpanded,
  resetContactDirty,
  resetAddressDirty,
  resetElectricityMeterDirty,
  resetGasMeterDirty,
  resetClientTabDirty,
} = SubscriptionFormSlice.actions

export default SubscriptionFormSlice.reducer
